import React, { useState, useEffect } from 'react';

const WhoWeAre = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Case Studies Data
  const caseStudies = [
    {
      title: 'Emerging Markets Venture Fund',
      category: 'Strategic Investment & Advisory',
      description: 'VBC assisted in building a unique fund strategy for a winners only fund with secondaries and pro-rata execution strategy. Enable a pipeline of LP\'s of individual HNI\'s Family Offices and FoF\'s to assist in the 1st close',
      image: 'https://images.unsplash.com/photo-1541506409807-29b68a3b631c'
    },
    {
      title: 'Fintech Cross Border Payments',
      category: 'Corporate Development & Advisory',
      description: 'VBC led the build out of a new country new product strategy to build out a payment platform strategy with a local in country partner with full ownership of identifying, assembling all moving parts including strategy, execution partners, JV partner, and regulatory liaison',
      image: 'https://images.unsplash.com/photo-1648161235886-32b6764917e6'
    },
    {
      title: 'Web 3 Crypto RWA Tokenisation',
      category: 'Innovation & Advisory',
      description: 'VBC led an innovative liquidity unlock innovation project with strategy to tokenise real world assets, obtain regulatory approval, liaise with distribution partners and overseeing the operationalisation of entire business',
      image: 'https://images.unsplash.com/photo-1644143379190-08a5f055de1d'
    },
    {
      title: 'Deep Tech Investments',
      category: 'Series B Investment',
      description: 'Series B co-investment via a SPV into a high growth disruptive deep tech startups with 5+ patents',
      image: 'https://images.unsplash.com/photo-1577110668630-c7e3e9b3acd6'
    },
    {
      title: 'Gen AI Startup',
      category: 'Venture Building',
      description: 'Work with the founding team to test the market and then fine tune the use case with few design partners to start building a product for financial services',
      image: 'https://images.unsplash.com/photo-1655393001768-d946c97d6fd1'
    },
    {
      title: 'Climate Tech',
      category: 'Investment and Advisory',
      description: 'Work with the founding team to close the pre-seed round and enable cross border international sales deals',
      image: 'https://images.unsplash.com/photo-1591468405321-dd43d3ea9cfe'
    },
    {
      title: 'Proptech Marketplace',
      category: 'Strategic Investment & Advisory',
      description: 'VBC led a syndicate to invest in the Seed round and acted as an accelerator for business development acquisition of listings and assisted the maturation of the product along with marketing strategy',
      image: 'https://images.unsplash.com/photo-1649770638609-4a938b9f648a'
    }
  ];

  // Create extended array for seamless infinite scrolling (for desktop 3-card view)
  const extendedCaseStudies = [
    ...caseStudies.slice(-2), // Last 2 items at the beginning
    ...caseStudies,
    ...caseStudies.slice(0, 2) // First 2 items at the end
  ];

  // Navigation functions for infinite loop
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % caseStudies.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => prev === 0 ? caseStudies.length - 1 : prev - 1);
  };

  // Auto-scroll carousel with infinite loop
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % caseStudies.length);
    }, 5000); // Auto-scroll every 5 seconds

    return () => clearInterval(interval);
  }, [caseStudies.length]);

  // Calculate transform based on mobile/desktop
  const getTransform = () => {
    if (isMobile) {
      return `translateX(-${currentSlide * 100}%)`;
    } else {
      // For desktop: offset by 2 items (since we added 2 items at the beginning)
      return `translateX(-${(currentSlide + 2) * 33.333}%)`;
    }
  };

  const values = [
    {
      title: 'Innovation First',
      description: 'We believe in the power of innovative thinking to solve complex problems and create lasting value.',
      image: 'https://images.unsplash.com/photo-1493612276216-ee3925520721'
    },
    {
      title: 'Collaborative Growth',
      description: 'Success is achieved through meaningful partnerships and shared expertise across our network.',
      image: 'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c'
    },
    {
      title: 'Long-term Impact',
      description: 'We focus on building sustainable businesses that create positive impact for all stakeholders.',
      image: 'https://images.unsplash.com/photo-1505816014357-96b5ff457e9a'
    },
    {
      title: 'Entrepreneurial Spirit',
      description: 'We embody the entrepreneurial mindset in everything we do, embracing calculated risks and bold moves.',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c'
    }
  ];

  return (
    <section id="who-we-are" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.1),transparent_50%)]" />
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Who <span className="gradient-text">We Are</span>
          </h2>
          <p className="text-xl text-blue-100 leading-relaxed">
            VBC is a dynamic network of <span className="text-secondary-300 font-semibold">entrepreneurs</span>, 
            <span className="text-secondary-300 font-semibold"> venture capitalists</span>, and 
            <span className="text-secondary-300 font-semibold"> investment bankers</span> united by our passion for building extraordinary companies.
          </p>
        </div>

        {/* Our Values Section */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-white text-center mb-12">
            Our <span className="gradient-text">Core Values</span>
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="glass rounded-2xl p-6 text-center hover:scale-105 transition-all duration-300 border border-white/10"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl overflow-hidden">
                  <img 
                    src={value.image} 
                    alt={value.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">{value.title}</h4>
                <p className="text-blue-100 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Case Studies Carousel Section */}
        <div className="mt-20">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h3 className="text-2xl font-bold text-white mb-4">
              Selective <span className="gradient-text">VBC Case Studies</span>
            </h3>
          </div>

          {/* Carousel Container with External Navigation */}
          <div className="relative w-full mx-auto flex items-center">
            {/* Left Navigation Arrow - Outside */}
            <button
              onClick={prevSlide}
              className="mr-2 md:mr-6 w-10 h-10 md:w-14 md:h-14 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110 z-10 flex-shrink-0"
            >
              <svg className="w-4 h-4 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Carousel Slides Container */}
            <div className="flex-1 overflow-hidden rounded-2xl min-w-0">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: getTransform() }}
              >
                {(isMobile ? caseStudies : extendedCaseStudies).map((study, index) => (
                  <div key={index} className="w-full md:w-1/3 flex-shrink-0 px-1 md:px-3">
                    <div className="glass rounded-2xl p-4 md:p-8 h-full transform transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-white/10 group">
                      {/* Image */}
                      <div className="relative overflow-hidden rounded-xl mb-3 md:mb-6">
                        <img
                          src={study.image}
                          alt={study.title}
                          className="w-full h-32 md:h-48 object-cover transform group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-2 md:top-4 left-2 md:left-4">
                          <span className="bg-secondary-500/90 backdrop-blur-sm text-white px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                            {study.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-2 md:space-y-4">
                        <h4 className="text-base md:text-xl font-bold text-white group-hover:text-secondary-300 transition-colors duration-300 line-clamp-2">
                          {study.title}
                        </h4>
                        <p className="text-blue-100 text-xs md:text-sm leading-relaxed line-clamp-3 md:line-clamp-none">
                          {study.description}
                        </p>
                      </div>

                      {/* Hover Effect Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/5 to-primary-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Navigation Arrow - Outside */}
            <button
              onClick={nextSlide}
              className="ml-2 md:ml-6 w-10 h-10 md:w-14 md:h-14 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300 border border-white/20 hover:scale-110 z-10 flex-shrink-0"
            >
              <svg className="w-4 h-4 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: caseStudies.length }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? 'bg-secondary-400 scale-125' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoWeAre;